import React, { FC, useEffect, useState } from 'react';
import { Link, useMatch, useResolvedPath } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import './sidebar.scss';

import { ReactComponent as Left } from 'design_system/src/static/icons/left-lg.svg';
import { ReactComponent as Right } from 'design_system/src/static/icons/right-lg.svg';
import { getUser } from 'localStorage/authStorage';
import {
	clearInventoryFilter,
	clearOzonerFilter,
	clearOzonioFilter,
	clearRepossessionsFilter,
	clearNotificationsFilter,
	clearDevicesFilter,
	clearOzocioFilter,
	clearWriteOffFilter,
	clearExternalInventoryFilter,
} from 'localStorage/filterStorage';
import { Roles } from 'models/ozonio/enums/ozonio.enum';
import { ProfileResponse } from 'models/auth/interfaces/auth.interface';
import { logout } from 'store/actions/user';
import {
	filterNavItems,
	getNavForUser,
	NavItem,
} from 'components/layouts/main/navigation/navConfig';

type Props = {
	to: string;
	name: string;
	Img: React.FunctionComponent<
		React.SVGProps<SVGSVGElement> & { title?: string | undefined }
	>;
};

export function RequireRole({
	children,
	authRoles,
}: {
	children: JSX.Element;
	authRoles: Roles[] | undefined;
}) {
	const user = getUser();
	if (
		authRoles &&
		(!user || !authRoles.some((role) => user.role.includes(role)))
	) {
		return null;
	}
	return children;
}

function getInitials(name?: string): string {
	if (!name) return '?';
	const parts = name.trim().split(/\s+/);
	if (parts.length >= 2) {
		return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
	}
	return name.slice(0, 2).toUpperCase();
}

const CustomLink: FC<Props> = ({ to, name, Img }) => {
	const resolved = useResolvedPath(to);
	const match = useMatch({ path: `${resolved.pathname}/*`, end: true });

	const cleanUp = () => {
		if (!match) {
			clearInventoryFilter();
			clearExternalInventoryFilter();
			clearOzonerFilter();
			clearOzocioFilter();
			clearOzonioFilter();
			clearRepossessionsFilter();
			clearNotificationsFilter();
			clearDevicesFilter();
			clearWriteOffFilter();
		}
	};

	return (
		<Link
			to={to}
			className={`nav_item ${match ? 'active' : ''}`}
			onClick={cleanUp}
		>
			<Img className="link_icon" aria-hidden="true" />
			<span className="link_text">{name}</span>
		</Link>
	);
};

function Sidebar() {
	const dispatch = useDispatch();
	const [user, setUser] = useState<ProfileResponse | null>(null);
	const [close, setClose] = useState(false);
	const [showLogout, setShowLogout] = useState(false);

	useEffect(() => {
		setUser(getUser());
	}, []);

	const handleSidebar = () => {
		setClose(!close);
	};

	const handleLogout = () => {
		dispatch(logout());
	};

	const mainNav = getNavForUser(user);

	return (
		<div className={`sidebar ${close ? 'close' : ''}`}>
			<div className="sidebar_logo">
				<img
					src={`${process.env.PUBLIC_URL || ''}/${
						close ? 'favicon.png' : 'sauron-logo.png'
					}`}
					alt="Sauron Logo"
					className="sidebar_logo_img"
				/>
				{!close && <span className="sidebar_logo_sub">Asset Tracking</span>}
			</div>

			<nav className="sidebar_nav">
				{mainNav.map((section) => {
					const items = filterNavItems(section.items, user);
					if (!items.length) return null;
					return (
						<div key={section.label}>
							<div className="nav_section">{section.label}</div>
							{items.map((ele: NavItem) => (
								<CustomLink
									to={ele.to}
									name={ele.name}
									Img={ele.img}
									key={ele.name}
								/>
							))}
						</div>
					);
				})}
			</nav>

			<div
				className="sidebar_footer"
				onClick={() => setShowLogout(!showLogout)}
				onKeyPress={() => setShowLogout(!showLogout)}
				role="button"
				tabIndex={0}
			>
				<div className="sidebar_user_row">
					<div className="sidebar_avatar">{getInitials(user?.name)}</div>
					<div className="sidebar_user_info">
						<div className="sidebar_user_name">{user?.name || 'Usuario'}</div>
					</div>
				</div>
				{showLogout && (
					<div className="sidebar_logout_popup">
						<div
							className="sidebar_logout_item"
							onClick={handleLogout}
							onKeyPress={handleLogout}
							role="button"
							tabIndex={0}
						>
							Cerrar sesión
						</div>
					</div>
				)}
			</div>

			<div
				className="sidebar_toggle"
				onClick={handleSidebar}
				onKeyPress={handleSidebar}
				role="button"
				tabIndex={0}
				aria-label={close ? 'Expandir sidebar' : 'Colapsar sidebar'}
			>
				{close ? <Right /> : <Left />}
			</div>
		</div>
	);
}

export default Sidebar;
